import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type {
  GetWikiChangelogResponseBody,
  GetWikiClaimResponseBody,
  GetWikiGraphResponseBody,
  GetWikiPageResponseBody,
  GetWikiPagesResponseBody,
  PostWikiClaimDecisionResponseBody,
  PostWikiCompileResponseBody,
  PostWikiResearchResponseBody,
  PostWikiSectionResponseBody,
} from "@app/types/api/me/wiki";
import { Err, Ok, type Result } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import type {
  WikiPageSummaryWireType,
  WikiProjectedSectionWireType,
  WikiRevisionWireType,
} from "@app/types/wiki";
import type { WikiGraphSourceNode } from "@app/types/wiki_graph";
import { useCallback } from "react";
import type { Fetcher } from "swr";

/**
 * The wiki this hook reads. Without a Pod it is the user's own; with one it is
 * that Project's, whose pages, claims and decisions have the same shapes under
 * a different owner — so one set of hooks and one set of components serve both.
 */
export function buildWikiUrl(workspaceId: string, spaceId?: string): string {
  return spaceId
    ? `/api/w/${workspaceId}/spaces/${spaceId}/wiki`
    : `/api/w/${workspaceId}/me/wiki`;
}

export function useWikiPages({
  owner,
  spaceId,
  disabled,
}: {
  owner: LightWorkspaceType;
  spaceId?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const pagesFetcher: Fetcher<GetWikiPagesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildWikiUrl(owner.sId, spaceId),
    pagesFetcher,
    {
      disabled,
      refreshInterval: (latest?: GetWikiPagesResponseBody) =>
        latest?.build?.running ? 5000 : 0,
    }
  );

  return {
    pages: data?.pages ?? emptyArray<WikiPageSummaryWireType>(),
    isBuildRunning: data?.build?.running ?? false,
    lastBuiltAt: data?.build?.lastBuiltAt ?? null,
    mailWindow: data?.build?.mailWindow ?? null,
    isPagesLoading: !disabled && !error && !data,
    isPagesError: !!error,
    mutatePages: mutate,
  };
}

export function useWikiPage({
  owner,
  spaceId,
  slug,
  disabled,
}: {
  owner: LightWorkspaceType;
  spaceId?: string;
  slug: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const pageFetcher: Fetcher<GetWikiPageResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    slug ? `${buildWikiUrl(owner.sId, spaceId)}/pages/${slug}` : null,
    pageFetcher,
    { disabled }
  );

  return {
    page: data?.page ?? null,
    links: data?.links ?? null,
    projection: data?.projection ?? emptyArray<WikiProjectedSectionWireType>(),
    peopleRoster: data?.peopleRoster ?? null,
    ownerInfobox: data?.ownerInfobox ?? null,
    personPhotoUrl: data?.personPhotoUrl ?? null,
    isPageLoading: !disabled && !!slug && !error && !data,
    isPageError: !!error,
    mutatePage: mutate,
  };
}

/** Why one claim is believed: its coverage, its evidence, and what it replaced. */
export function useWikiClaimExplanation({
  owner,
  spaceId,
  claimSId,
}: {
  owner: LightWorkspaceType;
  spaceId?: string;
  claimSId: string | null;
}) {
  const { fetcher } = useFetcher();
  const claimFetcher: Fetcher<GetWikiClaimResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    claimSId ? `${buildWikiUrl(owner.sId, spaceId)}/claims/${claimSId}` : null,
    claimFetcher
  );

  return {
    explanation: data?.explanation ?? null,
    isExplanationLoading: !!claimSId && !error && !data,
    isExplanationError: !!error,
  };
}

/**
 * Accept, correct or dismiss one claim. The decision lands synchronously; the
 * page markdown it renders into is rewritten by the next projection run, so
 * the caller is handed `projectionPending` rather than a page that has caught
 * up.
 */
export function useDecideWikiClaim({
  owner,
  spaceId,
  slug,
}: {
  owner: LightWorkspaceType;
  spaceId?: string;
  slug: string;
}) {
  const sendNotification = useSendNotification();
  const { mutatePage } = useWikiPage({ owner, spaceId, slug, disabled: true });

  return useCallback(
    async ({
      claimSId,
      decision,
      text,
    }: {
      claimSId: string;
      decision: "accept" | "correct" | "dismiss";
      text?: string;
    }): Promise<Result<PostWikiClaimDecisionResponseBody, Error>> => {
      try {
        const res = await clientFetch(
          `${buildWikiUrl(owner.sId, spaceId)}/claims/${claimSId}/${decision}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: text === undefined ? undefined : JSON.stringify({ text }),
          }
        );

        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: "Failed to update this claim",
            description: errorData.message,
          });
          return new Err(new Error(errorData.message));
        }

        const responseData: PostWikiClaimDecisionResponseBody =
          await res.json();
        await mutatePage();

        return new Ok(responseData);
      } catch (e) {
        const error = normalizeError(e);
        sendNotification({
          type: "error",
          title: "Failed to update this claim",
          description: error.message,
        });
        return new Err(error);
      }
    },
    [mutatePage, owner.sId, sendNotification, spaceId]
  );
}

export function useWikiChangelog({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const changelogFetcher: Fetcher<GetWikiChangelogResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `${buildWikiUrl(owner.sId)}/changelog`,
    changelogFetcher,
    { disabled }
  );

  return {
    revisions: data?.revisions ?? emptyArray<WikiRevisionWireType>(),
    isChangelogLoading: !disabled && !error && !data,
    isChangelogError: !!error,
    mutateChangelog: mutate,
  };
}

export function useWikiGraph({
  owner,
  slug,
  disabled,
}: {
  owner: LightWorkspaceType;
  // A person page's slug: that person's own map instead of the whole wiki's.
  slug?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const graphFetcher: Fetcher<GetWikiGraphResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `${buildWikiUrl(owner.sId)}/graph${slug ? `?${new URLSearchParams({ slug }).toString()}` : ""}`,
    graphFetcher,
    { disabled }
  );

  return {
    sources: data?.sources ?? emptyArray<WikiGraphSourceNode>(),
    isGraphLoading: !disabled && !error && !data,
    isGraphError: !!error,
    mutateGraph: mutate,
  };
}

/** Save one section as a human edit; the revision it writes is the changelog. */
export function useSaveWikiSection({
  owner,
  slug,
}: {
  owner: LightWorkspaceType;
  slug: string;
}) {
  const sendNotification = useSendNotification();
  const { mutatePage } = useWikiPage({ owner, slug, disabled: true });
  const { mutateChangelog } = useWikiChangelog({ owner, disabled: true });

  return useCallback(
    async ({
      anchor,
      body,
      summary,
    }: {
      anchor: string;
      body: string;
      summary: string;
    }): Promise<Result<PostWikiSectionResponseBody, Error>> => {
      try {
        const res = await clientFetch(
          `${buildWikiUrl(owner.sId)}/pages/${slug}/sections/${anchor}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body, summary }),
          }
        );

        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: "Failed to save section",
            description: errorData.message,
          });
          return new Err(new Error(errorData.message));
        }

        const responseData: PostWikiSectionResponseBody = await res.json();
        await mutatePage();
        await mutateChangelog();

        return new Ok(responseData);
      } catch (e) {
        const error = normalizeError(e);
        sendNotification({
          type: "error",
          title: "Failed to save section",
          description: error.message,
        });
        return new Err(error);
      }
    },
    [mutateChangelog, mutatePage, owner.sId, sendNotification, slug]
  );
}

/** Ask Friday to compile the wiki from the connected accounts. */
export function useCompileWiki({ owner }: { owner: LightWorkspaceType }) {
  const sendNotification = useSendNotification();
  const { mutatePages } = useWikiPages({ owner, disabled: true });
  const { mutateChangelog } = useWikiChangelog({ owner, disabled: true });

  return useCallback(async (): Promise<
    Result<PostWikiCompileResponseBody, Error>
  > => {
    try {
      const res = await clientFetch(`${buildWikiUrl(owner.sId)}/compile`, {
        method: "POST",
      });

      if (!res.ok) {
        const errorData = await getErrorFromResponse(res);
        sendNotification({
          type: "error",
          title: "Failed to compile the wiki",
          description: errorData.message,
        });
        return new Err(new Error(errorData.message));
      }

      const responseData: PostWikiCompileResponseBody = await res.json();
      sendNotification({
        type: "success",
        title: "Building your wiki",
        description: "Pages update here as they are written.",
      });
      await mutatePages();
      await mutateChangelog();

      return new Ok(responseData);
    } catch (e) {
      const error = normalizeError(e);
      sendNotification({
        type: "error",
        title: "Failed to compile the wiki",
        description: error.message,
      });
      return new Err(error);
    }
  }, [mutateChangelog, mutatePages, owner.sId, sendNotification]);
}

/**
 * Re-research one person page on demand. The nightly loop runs the same path on
 * a cadence, so what a human triggers here and what happens overnight cannot
 * come apart.
 */
export function useRefreshPersonResearch({
  owner,
  slug,
}: {
  owner: LightWorkspaceType;
  slug: string;
}) {
  const sendNotification = useSendNotification();
  const { mutatePage } = useWikiPage({ owner, slug, disabled: true });
  const { mutateChangelog } = useWikiChangelog({ owner, disabled: true });

  return useCallback(async (): Promise<
    Result<PostWikiResearchResponseBody, Error>
  > => {
    try {
      const res = await clientFetch(
        `${buildWikiUrl(owner.sId)}/pages/${slug}/research`,
        { method: "POST" }
      );

      if (!res.ok) {
        const errorData = await getErrorFromResponse(res);
        sendNotification({
          type: "error",
          title: "Failed to update this page",
          description: errorData.message,
        });
        return new Err(new Error(errorData.message));
      }

      const responseData: PostWikiResearchResponseBody = await res.json();
      await mutatePage();
      await mutateChangelog();

      if (!responseData.refreshed) {
        sendNotification({
          type: "info",
          title: "Nothing new found",
          description:
            "The search turned up nothing this page does not already say.",
        });
      }

      return new Ok(responseData);
    } catch (e) {
      const error = normalizeError(e);
      sendNotification({
        type: "error",
        title: "Failed to update this page",
        description: error.message,
      });
      return new Err(error);
    }
  }, [mutateChangelog, mutatePage, owner.sId, sendNotification, slug]);
}
